import { zodResolver } from "@hookform/resolvers/zod";
import { useTheme } from "@mui/material";
import { useUserManagementServiceV1 } from "@rw1/api-services";
import { UserManagement_V1 } from "@rw1/auto-generated-clients";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { FC, useMemo } from "react";
import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { ConfirmationDialog, LabeledTextField } from "../../../components";
import { useIsMobile } from "../../../hooks/useIsMobile";
import { useLocalization } from "../../../hooks/useLocalization";
import { PENDING_INVITATIONS_QUERY_KEY } from "../hooks/usePendinginvitations";
import { InviteUserFormData, inviteUserSchema } from "./inviteUserSchema";
import { FormDialogControls } from "./types";

export const InviteUserForm: FC<FormDialogControls> = ({
  setSuccessMessage,
  setIsSuccessDialogOpen,
  setIsAddUserDialogOpen,
}) => {
  const theme = useTheme();
  const { t } = useLocalization();
  const userManagementClient = useUserManagementServiceV1();
  const queryClient = useQueryClient();
  const { companyId } = useParams();
  const isMobile = useIsMobile();
  const actionText = useMemo(
    () =>
      isMobile
        ? t("users.inviteUserForm.send")
        : t("users.inviteUserForm.sendInvitation"),
    [isMobile, t],
  );

  const form = useForm<InviteUserFormData>({
    resolver: zodResolver(inviteUserSchema),
    defaultValues: {
      email: "",
    },
  });

  const inviteUserMutation = useMutation({
    mutationFn: (invitation: UserManagement_V1.InvitationWithoutId) => {
      if (!companyId) {
        throw new Error("Company ID not found");
      }

      return userManagementClient.inviteUser(companyId, invitation);
    },
    onSuccess: () => {
      form.reset();
      void queryClient.invalidateQueries({
        queryKey: [PENDING_INVITATIONS_QUERY_KEY],
      });
      setIsSuccessDialogOpen(true);
      setSuccessMessage(t("users.inviteUserForm.successMessage"));
      setIsAddUserDialogOpen(false);
    },
    onError: () => {
      form.reset();
      void queryClient.invalidateQueries({
        queryKey: [PENDING_INVITATIONS_QUERY_KEY],
      });
      setIsSuccessDialogOpen(true);
      setSuccessMessage(t("users.inviteUserForm.successMessage"));
      setIsAddUserDialogOpen(false);
    },
  });

  const onSubmit = (data: InviteUserFormData) => {
    const invitation = new UserManagement_V1.InvitationWithoutId({
      inviteeEmail: data.email,
      isV2: true,
    });

    inviteUserMutation.mutate(invitation);
  };

  const handleFormSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    void form.handleSubmit(onSubmit)();
  };

  const handleCancel = () => {
    form.reset();
    setIsAddUserDialogOpen(false);
  };

  return (
    <>
      <ConfirmationDialog.Content>
        <form autoComplete="off" noValidate onSubmit={handleFormSubmit}>
          <LabeledTextField
            {...form.register("email")}
            error={!!form.formState.errors.email}
            helperText={form.formState.errors.email?.message}
            label={t("users.inviteUserForm.emailAddress")}
            placeholder={t("users.inviteUserForm.emailPlaceholder")}
            required
            type="email"
          />
        </form>
      </ConfirmationDialog.Content>
      <ConfirmationDialog.Actions>
        <ConfirmationDialog.Button
          onClick={handleCancel}
          sx={{
            color: theme.colors.primary[5],
          }}
          variant="secondary"
        >
          {t("users.inviteUserForm.cancel")}
        </ConfirmationDialog.Button>
        <ConfirmationDialog.Button
          disabled={inviteUserMutation.isPending}
          isLoading={inviteUserMutation.isPending}
          loadingText={t("users.inviteUserForm.sending")}
          onClick={handleFormSubmit}
          variant="contained"
        >
          {actionText}
        </ConfirmationDialog.Button>
      </ConfirmationDialog.Actions>
    </>
  );
};
